import { useT } from "@/i18n/provider"
import type { AuditEntry } from "@/lib/types"

/** One field's diff: either an old→new pair, or a redacted "changed" marker. */
export type AuditChange = { old?: unknown; new?: unknown; changed?: boolean }

/** The `details.changes` map for an entry, or null when there is nothing to show. */
export function auditChanges(entry: AuditEntry): Record<string, AuditChange> | null {
  const changes = (entry.details as { changes?: Record<string, AuditChange> } | null)?.changes
  if (!changes || Object.keys(changes).length === 0) return null
  return changes
}

function format(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—"
  if (typeof value === "boolean") return value ? "true" : "false"
  return String(value)
}

/** Renders a field-by-field diff: `field  old → new`, or `field  changed` for redacted fields. */
export function AuditChanges({ changes }: { changes: Record<string, AuditChange> }) {
  const t = useT()
  return (
    <ul className="space-y-1">
      {Object.entries(changes).map(([field, change]) => (
        <li key={field} className="flex flex-wrap items-baseline gap-x-2">
          <span className="font-medium">{field}</span>
          {change.changed ? (
            <span className="text-muted-foreground italic">{t("activity.changes.redacted")}</span>
          ) : (
            <span className="text-muted-foreground">
              <span className="line-through">{format(change.old)}</span>
              {" → "}
              <span className="text-foreground">{format(change.new)}</span>
            </span>
          )}
        </li>
      ))}
    </ul>
  )
}
